<template>
    <div>
        <input
            ref="refSearchInput"
            v-model="searchValue"
            type="search"
            autocomplete="nope"
            :maxlength="MAX_LENGTH"
            :aria-label="trans('SEARCH')"
            class="autoSearch__input"
            :class="{ 'has-historyButton': allowBrowseHistory }"
            :placeholder="trans('HOMEPAGE_SEARCHBAR_PLACEHOLDER_2016')"
            name="searchBar"
            @input="inputChange"
            @keydown.enter.prevent="setSearchValue"
            @keydown.up.prevent="selectUp"
            @keydown.down.prevent="selectDown"
            @keydown.esc.prevent="showAutocomplete(false)"
            @focus="showAutocomplete(true)"
            @blur="showAutocomplete(false)" />

        <transition name="fade">
            <div
                v-if="showAutoBox"
                :class="isMobile ? 'autocompleteList--mobile' : 'autocompleteList'">
                <div class="flex flex__justifyContent--spaceBetween booleanSearch__box">
                    <p
                        class="booleanSearch__text"
                        v-html="trans('BOOLEAN_SEARCH_TEXT')"></p>
                    <a
                        rel="noopener"
                        target="_blank"
                        class="link booleanSearch__link"
                        :href="locale === 'de' ? linkDE : linkEN">
                        {{ trans('BOOLEAN_SEARCH_LINK_TEXT') }}
                    </a>
                </div>
                <div
                    v-if="showHistory"
                    id="searchOperatorInfo"
                    :class="isMobile ? 'autocompleteList__operatorInfo--mobile' : 'autocompleteList__operatorInfo'">
                    {{ trans('AUTO_COMPLETE__RECENT_SEARCHS') }}
                </div>
                <div
                    v-else-if="searchArray.length > 0 && showSuggestionText"
                    id="searchOperatorInfo"
                    :class="isMobile ? 'autocompleteList__operatorInfo--mobile' : 'autocompleteList__operatorInfo'">
                    {{ trans('AUTO_COMPLETE__SUGGESTIONS') }}
                </div>
                <ul
                    v-if="showHistory"
                    class="autocompleteList__resultsHistory">
                    <li
                        v-for="(value, key, index) in histories"
                        :key="key"
                        :class="{ 'autocompleteList__item--selected': index === selectedResult }"
                        class="autocompleteList__resultsItem">
                        <div
                            v-if="value.entry.q !== ''"
                            class="autocomplete__history__item"
                            :class="{ 'autocomplete__history__item--mobile': isMobile }">
                            <svg class="stat__svgIcon autocompleteList__item__icon padding-right-5">
                                <use xlink:href="#iconClock" />
                            </svg>
                            <span
                                class="autocomplete__history__item--width"
                                @mousedown="selectItem(value.entry.q)">
                                {{ value.entry.q }}
                            </span>
                            <button
                                type="button"
                                @click="deleteClickEvents(value.entryUniqueIdEncrypted)">
                                <span class="sr-only"> delete history</span>
                                <svg class="stat__svgIcon autocompleteList__item__icon">
                                    <use xlink:href="#iconCrossBold" />
                                </svg>
                            </button>
                        </div>
                    </li>
                </ul>
                <ul
                    v-else-if="searchArray.length > 0"
                    class="autocompleteList__results">
                    <li
                        v-for="(item, index) in searchArray"
                        :key="item"
                        :class="{ 'autocompleteList__item--selected': index === selectedResult, 'autocompleteList__resultsItem--mobile' : isMobile }"
                        class="autocompleteList__resultsItem"
                        @mousedown="selectItem(item)">
                        <div class="autocompleteList__resultsItem__icon">
                            <svg
                                v-if="isMobile"
                                class="text-color--gray-blue"
                                height="20"
                                width="20"
                                aria-hidden="true">
                                <use href="#iconMagnifyingGlassRegular" />
                            </svg>
                            <a
                                :class="{ 'autocompleteList__item--mobile' : isMobile }"
                                class="autocompleteList__item autocompleteList__item--linkReset"
                                v-html="highlightSearch(item, searchValue)">
                            </a>
                        </div>
                    </li>
                </ul>
            </div>
        </transition>
        <button
            @click="clearInput"
            class="autocomplete__deleteIcon searchIcon__right">
            <svg
                v-if=" searchValue.length > 0"
                height="16"
                width="16"
                aria-hidden="true">
                <use href="#iconCrossCircleBoldInverted" />
            </svg>
        </button>
    </div>
</template>

<script>
    // Since there is a difference between what we show to the user and what we are searching for,
    // there are two variables:
    //      searchDisplay       shows in the input field
    //      searchValue         real field value for search request
    import { defineComponent, ref, computed, onMounted, getCurrentInstance } from 'vue';
    import { useActions, useGetters } from 'composables/useVuex';
    import Translator from 'tools/trans.js';
    import { isMobile } from 'tools/isMobile';
    import { statHistoryCache } from 'tools/historycache';
    import { pushToDataLayer } from 'search/dataLayer';


    export default defineComponent({
        name: 'SearchAutocomplete',
        props: {
            styles: { type: Object, default: () => ({}) },
            submitSelfAction: { type: Boolean, default: false },
        },
        data() {
            return {
                isMobile: isMobile(),
                linkDE: 'https://de.statista.com/getting-started/erfolgreich-arbeiten-mit-statista-suchfunktion',
                linkEN: 'https://www.statista.com/getting-started/working-with-statista-search-function',
                locale: window.stGlobals.locale,
                // historyEntry: window.stGlobals.historyEntry,
            };
        },
        computed: {
            showSuggestionText() {
                return !(this.isMobile && !this.user);
            },
            showAutoBox() {
                return (
                    this.isMobile && this.show
                ) || (
                    this.show
                    && (
                        this.showHistory
                        || (this.searchArray.length > 0 && this.query.length > 0)
                    )
                );
            },
        },
        watch: {
            searchValue(newVal) {
                // this.updateQuery(newVal);
                if (newVal === '') {
                    this.init();
                }
            },
        },
        methods: {
            trans: Translator.trans,
            init() {
                if (this.user) {
                    this.historycache.getHistoryDataJson(
                        this.userId,
                        'searchhistory',
                        '/profile/searchhistorydata/',
                    );
                }
            },
            deleteClickEvents(selected) {
                if (this.user) {
                    if (Object.keys(this.histories).length > 1) {
                        this.setShow(true);
                    }
                    this.historycache.deleteEntry(this.userId, selected, 'searchhistory');
                    this.historycache.getHistoryDataJson(
                        this.userId,
                        'searchhistory',
                        '/profile/searchhistorydata/',
                    );
                }
            },
            clearInput() {
                this.searchValue = '';
            },
        },
        mounted() {
            this.init();
        },

        setup() {
            onMounted(() => {
                getCurrentInstance();
            });
            let debounce = null;

            const MAX_LENGTH = 500;
            const user = window.stGlobals.user;
            const userId = window.stGlobals.userId;

            const searchDisplay = ref(''),
                  searchValue = ref(''),
                  refSearchInput = ref(null),
                  selectedResult = ref(null),
                  requestTimeout = 250,
                  widthStyle = computed(() => {
                      if (!refSearchInput.value) {
                          return '';
                      }

                      return `width: ${ refSearchInput.value.offsetWidth + 25 }px;`;
                  }),
                  gettingStarted = ref('');

            const historycache = statHistoryCache();
            const historyLimit = 10;

            const showHistory = computed(() => {
                if (user && Object.keys(getters.histories.value).length > 0 && searchValue.value === '') {
                    return true;
                } else {
                    return false;
                }
            });

            const getters = {
                ...useGetters(['language', 'searchQueryParameters']),
                ...useGetters('search', ['show', 'query', 'searchArray', 'histories', 'entry', 'entryUniqueId', 'totalFound']),
                ...useGetters('globalSettings', ['allowBrowseHistory', 'hrefBrowseHistory']),
            };

            const actions = {
                ...useActions('search', ['setShow', 'setValue', 'setParamFunc', 'startSearch', 'resetPageNumber', 'resetFilters', 'resetTagsPage']),
            };

            // debounce input change
            function inputChange() {
                // delete search index in list
                selectedResult.value = null;

                // delete old searches (timeouts)
                clearTimeout(debounce);

                // now put in search with new timeout
                debounce = setTimeout(() => {
                    actions.setValue({ value: searchValue.value, timeout: requestTimeout });
                }, 300);
            }
            // function updateQuery(newValue) {
            //     actions.setValue({ value: newValue, timeout: requestTimeout });
            // }
            // send value to store
            async function selectItem(item) {
                searchValue.value = item;

                await actions.setValue({ value: item });

                await actions.resetPageNumber();
                await actions.setParamFunc();
                await actions.resetTagsPage();
                await actions.startSearch({ noFigures: false, noReport: false, noTopic: false, resetTags: true, history: false });
                document.body.scrollTop = 0; // For Safari
                document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE
                // push to data layer
                setTimeout(() => {
                    pushToDataLayer();
                }, 5000);
                if (user) {
                    historycache.addEntry(
                        userId,
                        getters.entryUniqueId.value,
                        getters.entry.value,
                        'searchhistory',
                        historyLimit
                    );
                }

            }

            // highlight search for each entry in list
            function highlightSearch(item) {
                const regEx = new RegExp(searchValue.value, 'gi');

                return item.replace(regEx, function() { return `<span class="autocompleteList__item--highlightItem">${ searchValue.value }</span>`; });
            }

            async function selectDown(_event) {
                if (showHistory.value) {
                    if (selectedResult.value >= Object.keys(getters.histories.value).length - 1 || selectedResult.value > 0) {
                        selectedResult.value = selectedResult.value === null
                            ? Object.keys(getters.histories.value).length - 1
                            : selectedResult.value - 1;
                    } else {
                        selectedResult.value = Object.keys(getters.histories.value).length - 1;
                    }
                } else {
                    if (selectedResult.value >= getters.searchArray.value.length - 1 || selectedResult.value < 0) {
                        selectedResult.value = 0;
                    } else {
                        selectedResult.value = selectedResult.value === null ? 0 : selectedResult.value + 1;
                    }
                }
            }

            async function selectUp(_event) {
                if (showHistory.value) {
                    selectedResult.value = selectedResult.value === null
                        ? Object.keys(getters.histories.value).length - 1
                        : selectedResult.value + 1;
                    if (selectedResult.value > -1) {
                        searchDisplay.value = getters.histories.value[selectedResult.value];
                    }
                } else {
                    selectedResult.value = selectedResult.value === null
                        ? getters.searchArray.value.length - 1
                        : selectedResult.value - 1;
                    if (selectedResult.value > -1) {
                        searchDisplay.value = getters.searchArray.value[selectedResult.value];
                    }
                }
            }

            function showAutocomplete(mode) {
                actions.setShow(mode);
            }

            async function setSearchValue() {
                let searchVal = '';
                if (showHistory.value) {
                    const newQuery = getters.histories.value[Object.keys(getters.histories.value)[selectedResult.value]];
                    searchVal = newQuery.entry?.q ?? searchValue.value;
                } else {
                    searchVal = getters.searchArray.value?.[selectedResult.value] ?? searchValue.value;
                }

                if (searchVal) {
                    actions.setShow(false);

                    searchValue.value = searchVal;
                    await actions.setValue({ value: searchValue.value });
                    await actions.resetPageNumber();
                    await actions.setParamFunc();
                    await actions.startSearch({ noFigures: false, noReport: false, noTopic: false, resetTags: true, history: false });
                    document.body.scrollTop = 0; // For Safari
                    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE
                    // push to data layer
                    setTimeout(() => {
                        pushToDataLayer();
                    }, 5000);
                    await actions.resetTagsPage();

                    // activate blur manually
                    refSearchInput.value.blur();
                    if (user) {
                        historycache.addEntry(
                            userId,
                            getters.entryUniqueId.value,
                            getters.entry.value,
                            'searchhistory',
                            historyLimit
                        );
                    }
                }
            }

            async function resetSearchInput() {
                searchValue.value = '';
                await actions.setValue({ value: searchValue.value });
                await actions.resetPageNumber();
                await actions.setParamFunc();
                await actions.startSearch({ noFigures: false, noReport: false, noTopic: false, resetTags: true, history: false });
            }

            onMounted(() => {
                searchValue.value = getters.query.value;
                gettingStarted.value = document.querySelector('[data-gettingstartedlink]').dataset.gettingstartedlink;
            });

            return {
                MAX_LENGTH,
                user,
                userId,
                historycache,
                showHistory,
                searchDisplay,
                searchValue,
                inputChange,
                // updateQuery,
                refSearchInput,
                widthStyle,
                selectItem,
                selectUp,
                selectDown,
                setSearchValue,
                highlightSearch,
                showAutocomplete,
                selectedResult,
                gettingStarted,
                resetSearchInput,
                ...actions,
                ...getters,
            };
        },
    });
</script>
